import { ErrorMessage, Field, useField } from "formik";
import React from "react";
import { Col, FormGroup, Label } from "reactstrap";

type CustomInputProps = {
  as?: string;
  name: string;
  rows?: string;
  cols?: string;
  type?: string;
  label?: string;
  errors?: any;
  multiple?: boolean;
  disabled?: boolean;
  placeholder?: string;
  showPassword?: boolean;
  fieldClassName?: string;
  setShowPassword?: React.Dispatch<React.SetStateAction<boolean>>;
  handleOnChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void; // Custom onChange handler
  errorComponent?: React.ComponentType<any> | string;
  min?: string;
  max?: string;
  handleKeyDown?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleOnPaste?: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

export const CustomInput = ({
  as,
  min,
  max,
  name,
  type,
  rows,
  cols,
  label,
  errors,
  disabled,
  placeholder,
  showPassword,
  handleOnChange,
  setShowPassword,
  fieldClassName = "",
  errorComponent = "div",
  multiple = false,
  handleKeyDown,
  handleOnPaste,
}: CustomInputProps) => {
  const [field, { touched, error }] = useField(name);

  // Destructure onChange from Formik's field object
  const { onChange: onFieldChange, ...restField } = field;

  // Combine Formik's onChange and custom handleOnChange
  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (handleOnChange) {
      handleOnChange(event); // Call custom onChange
    }
    onFieldChange(event); // Call Formik's onChange
  };

  const renderError = () =>
    error ? ( // Show error only if touched
      <ErrorMessage
        name={name}
        component={errorComponent}
        className="invalid-feedback"
      />
    ) : null;

  const renderPasswordField = () => (
    <div className="form-input position-relative">
      <Field
        type={type}
        className={fieldClassName}
        {...field}
        placeholder={placeholder}
        multiple={multiple}
      />
      {renderError()}
      <div
        className={`show-hide me-3 ${
          errors?.password ? "d-none" : "d-block "
        } ${touched && errors?.password ? "hide_text" : ""}`}
      >
        <span
          onClick={() => setShowPassword && setShowPassword((prev) => !prev)}
          className={!showPassword ? "show" : ""}
        />
      </div>
    </div>
  );

  return (
    <FormGroup>
      {label && <Label className="col-form-label">{label}</Label>}
      {["password", "confirmPassword"].includes(name) ? (
        renderPasswordField()
      ) : (
        <>
          <Field
            as={as}
            type={type}
            className={touched ? fieldClassName : "form-control f-12"}
            rows={rows}
            cols={cols}
            onChange={handleChange}
            disabled={disabled}
            placeholder={placeholder}
            min={type === "time" ? min : undefined}
            max={type === "time" ? max : undefined}
            onKeyDown={(event: React.ChangeEvent<HTMLInputElement>) =>
              type === "time" && handleKeyDown
                ? handleKeyDown(event)
                : undefined
            }
            onPaste={(event: React.ChangeEvent<HTMLInputElement>) =>
              type === "time" && handleOnPaste
                ? handleOnPaste(event)
                : undefined
            }
            {...restField}
          />
          {renderError()}
        </>
      )}
    </FormGroup>
  );
};

export const InputField = ({
  xs,
  sm,
  md = "6",
  lg,
  xl,
  as,
  min,
  max,
  rows,
  cols,
  name,
  type,
  label,
  errors,
  touched,
  disabled,
  placeholder,
  showPassword,
  setShowPassword,
  handleOnChange,
  multiple,
  handleKeyDown,
  handleOnPaste,
}: {
  xs?: string;
  sm?: string;
  md?: string;
  lg?: string;
  xl?: string;
  as?: string;
  name: string;
  label?: string;
  type?: string;
  rows?: string;
  cols?: string;
  min?: string;
  max?: string;
  disabled?: boolean;
  placeholder?: string;
  showPassword?: boolean;
  multiple?: boolean;
  touched?: { [key: string]: boolean } | any;
  errors: any | { [key: string]: string | undefined };
  setShowPassword?: React.Dispatch<React.SetStateAction<boolean>>;
  handleOnChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void; // Custom onChange handler
  handleKeyDown?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleOnPaste?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
  return (
    <Col xs={xs} sm={sm} md={md} lg={lg} xl={xl}>
      <CustomInput
        as={as}
        label={label}
        name={name}
        type={type}
        fieldClassName={`form-control f-12  ${
          (typeof errors === "string" && errors === name && "is-invalid") ||
          (errors[name] && touched && touched[name])
            ? "is-invalid"
            : ""
        }`}
        rows={rows}
        cols={cols}
        errorComponent="div"
        disabled={disabled}
        handleOnChange={handleOnChange}
        showPassword={showPassword}
        setShowPassword={setShowPassword}
        placeholder={placeholder}
        errors={errors}
        multiple={multiple}
        min={min}
        max={max}
        handleKeyDown={handleKeyDown}
        handleOnPaste={handleOnPaste}
      />
    </Col>
  );
};
